<template>
  <div class="image-editor">
    <div ref="wrapper" :class="wrapperClasses">
      <canvas
        ref="canvas"
        v-drawable-canvas="onDraw"
        class="image-editor__canvas"
      />
      <div v-show="loading || saving" class="image-editor__loading">
        <img class="image-editor__loading__logo" src="/images/logo300.png" alt="Loading...">
      </div>
    </div>
    <div class="image-editor__controls">
      <button class="action-btn image-editor__controls__icon-btn" :disabled="!hasBeenEdited" @click="undoEdits()">
        <Icon>{{ cancelMedium }}</Icon>
      </button>
      <button
        class="action-btn ml10 image-editor__controls__icon-btn"
        :disabled="loading || saving"
        @click="rotateImage()"
      >
        <Icon>{{ rotate }}</Icon>
      </button>
      <button class="action-btn red ml50 image-editor__controls__icon-btn" @click="removeImage()">
        <Icon>{{ deleteMedium }}</Icon>
      </button>
      <button class="action-btn green ml10 image-editor__controls__icon-btn" @click="done()">
        <Icon>{{ tickMedium }}</Icon>
      </button>
    </div>
  </div>
</template>

<script>
/**
 * Image editor using the file system
 *
 * @vue-prop {Object} value - File ; Resource that will be loaded
 * @vue-prop {String} [pathname] - Force pathname given to potential uploaded files
 *
 * @vue-event {} input
 * @vue-event {} edit
 * @vue-event {} remove
 */
// Import components
import directiveDrawableCanvas from '@/directives/drawable-canvas';
import Icon from '@/components/icon.vue';
// Import Vuex utils
import { UPLOAD_FILE, FETCH_FILE_DATA } from '@/store/modules/file/action-types';
// Import utils
import { rotateCanvasImage, ACCEPTED_PATHNAMES } from '@/utils/file';
import {
  cancelMedium,
  deleteMedium,
  rotate,
  tickMedium,
} from '@/assets/icons/';

export default {

  name: 'ImageEditor',

  components: {
    Icon,
  },

  directives: {
    DrawableCanvas: directiveDrawableCanvas,
  },

  props: {
    value: {
      type: Object,
      required: true,
    },
    pathname: {
      type: String,
      default: undefined,
      validator: v => ACCEPTED_PATHNAMES.includes(v),
    },
  },

  data() {
    return {
      cancelMedium,
      deleteMedium,
      rotate,
      tickMedium,
      imageElmt: null,
      hasBeenEdited: false,
      loading: false,
      saving: false,
      savingPromise: null,
      isWrapperWider: false,
      imageLoader: new Image(),
    };
  },

  computed: {
    wrapperClasses() {
      return {
        'image-editor__wrapper': true,
        'image-editor__wrapper--taller': !this.isWrapperWider,
        'image-editor__wrapper--wider': !!this.isWrapperWider,
      };
    },
    /** Blob: File data */
    imgData() {
      return this.$store.state.file.fileData[`${this.value.id}`];
    },
  },

  watch: {
    value() {
      this.loadImage();
    },
    hasBeenEdited(newVal) {
      this.$emit('edit', newVal);
    },
    imgData() {
      this.imageLoader.src = this.imgData;
    },
  },

  created() {
    this.imageLoader.onload = () => {
      const { canvas } = this.$refs;
      // If there is no canvas, component is probably destroyed
      if (!canvas) return;
      canvas.height = this.imageLoader.naturalHeight;
      canvas.width = this.imageLoader.naturalWidth;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(this.imageLoader, 0, 0);
      this.resizeAction();
      this.loading = false;
    };
    if (this.imgData) {
      this.imageLoader.src = this.imgData;
    }
  },

  mounted() {
    this.loadImage();
    window.addEventListener('resize', this.resizeAction, false);
    window.addEventListener('orientationchange', this.resizeAction, false);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.resizeAction, false);
    window.removeEventListener('orientationchange', this.resizeAction, false);
  },


  methods: {
    /**
     * Re-evaluate `this.isWrapperWider` value ;
     * Evaluate wrapper ideal size for displaying content with correct ratio
     * @param {Number} childWidth - Width of the wrapper's child
     * @param {Number} childHeight - Height of the wrapper's child
     */
    evalWrapperFit(childWidth, childHeight) {
      const { wrapper } = this.$refs;

      const parentHeight = wrapper.clientHeight;
      const parentWidth = wrapper.clientWidth;
      if (parentHeight > 0 && parentWidth > 0) {
        this.isWrapperWider = (!childHeight || childWidth / childHeight > parentWidth / parentHeight);
      }
    },
    /**
     * Update canvas and image display
     * Need to be call after any wrapper/image/canvas resize (image change included)
     */
    resizeAction() {
      const { height, width } = this.$refs.canvas;
      this.evalWrapperFit(width, height);
    },
    loadImage(force = false) {
      this.loading = true;
      this.hasBeenEdited = false;
      this.$store.dispatch(`file/${FETCH_FILE_DATA}`, { fileId: this.value.id, force });
    },
    /**
     * Restore initial image ; Cancel modification
     */
    undoEdits() {
      this.loadImage(true);
    },
    /**
     * Rotate selected picture and drawing canvas
     * @param {Number} rotateFactor - Number of rotation by 90 degree (by ROTATE_INCR constant)
     */
    rotateImage(rotateFactor) {
      const { canvas } = this.$refs;
      rotateCanvasImage(canvas, rotateFactor);
      this.hasBeenEdited = true;
      this.resizeAction();
    },
    /**
     * Save the new image on the file system
     * @return {Promise} promise containing the new file created
     */
    async saveImage() {
      const { canvas } = this.$refs;
      if (this.saving) {
        return this.savingPromise;
      }
      if (!this.hasBeenEdited || this.loading) {
        return Promise.resolve(this.value);
      }
      const mimetype = this.value.type || 'image/jpeg';
      this.saving = true;
      const oldFileId = this.value.id;
      this.savingPromise = new Promise((resolve) => {
        canvas.toBlob(resolve, mimetype);
      })
        .then(file => this.$store.dispatch(
          `file/${UPLOAD_FILE}`,
          Object.assign({}, this.value, { file, pathname: this.pathname }),
        ))
        .then((fileDetails) => {
          if (oldFileId === this.value.id) {
            this.$emit('input', fileDetails);
          }
          this.$emit('save', { file: fileDetails, oldFileId });
          return fileDetails;
        })
        .finally(() => {
          this.saving = false;
        });
      return this.savingPromise;
    },
    removeImage() {
      this.$emit('remove');
    },
    done() {
      this.saveImage();
      this.$emit('done');
    },
    onDraw() {
      this.hasBeenEdited = true;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.image-editor {
  height: 100%;
  width: 100%;
  padding: 0px .5rem;
  box-sizing: border-box;

  &__wrapper {
    position: relative;
    height: 100%;
    width: 100%;
    overflow: hidden;

    &--taller > .image-editor__canvas {
      height: 100%;
      width: auto;
    }

    &--wider > .image-editor__canvas {
      height: auto;
      width: 100%;
    }
  }
  &__canvas {
    position: absolute;
    left: -100%;
    right: -100%;
    top: -100%;
    bottom: -100%;
    margin: auto;
    height: 0;
    width: 0;
    touch-action: none;
    border-radius: $main-border-radius;
  }

  &__controls {
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);

    &__icon-btn {
      &.action-btn {
        border-radius: 100px;
        padding: .65rem;

        .icon {
          width: 1.8rem;
          height: 1.8rem;
          margin: 0;
        }
      }
    }
  }

  &__loading {
    background-color: rgba(0,0,0,0.8);
    height: 100%;
    position: absolute;
    width: 100%;

    &__logo {
      animation: rotation 2s infinite linear;
      background: #ffffff;
      border-radius: 50px;
      box-shadow: 0px 0px 25px 15px #ffffff;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      margin: auto;
      width: 60px;
      height: 60px;
      max-width: 20%;
      max-height: 20%;
    }
  }
}
</style>
